import { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { EditorState, type Plugin } from '@atlaskit/editor-prosemirror/state';
import { Decoration, EditorView } from '@atlaskit/editor-prosemirror/view';

import { createCommand } from './in-editor-streaming-plugin-factory';
import { expandPositionsToDocDepth } from './utils/expand-positions';

const createHiddenNodeDecoration = (from: number, to: number) => {
	return Decoration.node(
		from,
		to,
		{
			class: 'ai-inline-streaming-hidden',
			'data-testid': 'ai-inline-streaming-hidden',
		},
		{ key: 'inlineStreamingDecoration' },
	);
};

export const createAIResponseDecoration = (
	[startPos, endPos]: [number, number],
	state: EditorState,
) => {
	const widgetElement = document.createElement('span');
	widgetElement.setAttribute('data-testid', `ai-in-editor-widget`);
	widgetElement.className = `ai-in-editor-widget`;

	const [start, end] = expandPositionsToDocDepth({
		doc: state.doc,
		startPos,
		endPos,
	});

	const offset = startPos - start;
	const originalContent = state.doc.slice(start, end);

	const schema = new Schema({
		nodes: state.schema.spec.nodes,
		marks: state.schema.spec.marks,
	});

	const originalCutContent = PMNode.fromJSON(schema, state.doc.cut(start, end).toJSON());
	/**
	 * We want to inherit any node views from the parent document and retain all the functionality
	 */
	const innerEditorState = EditorState.create({
		doc: originalCutContent || undefined,
		schema,
	}).reconfigure({
		plugins: state.plugins.reduce((acc, plugin) => {
			if (plugin.props.nodeViews) {
				acc.push({
					...plugin,
					getState: plugin.getState,
					props: {
						nodeViews: plugin.props.nodeViews,
					},
				});
			} else {
				acc.push({
					...plugin,
					spec: {
						...plugin.spec,
						view: () => ({}),
						filterTransaction: () => true,
						appendTransaction: () => undefined,
					},
					getState: plugin.getState,
				});
			}
			return acc;
		}, [] as Plugin[]),
	});

	const innerEditorView = new EditorView(widgetElement, {
		// You can use any node as an editor document
		state: innerEditorState,
	});

	const widget = Decoration.widget(start, widgetElement, {
		side: 0,
		key: `aiInEditorWidgetDecoration`,
	});

	return {
		widget,
		offset,
		innerEditorView,
		originalContent,
	};
};

export const getHiddenRangeDecorations = ({
	state,
	initialPositions,
}: {
	state: EditorState;
	initialPositions: [number, number];
}) => {
	/**
	 * We add selection highlight decoration based on the current selection state of the document and the config item in the command palette
	 * If the selection is empty and the document is not empty -> we decorate the entire document unless the config item specifies otherwise
	 */

	const { startPos, endPos } = { startPos: initialPositions[0], endPos: initialPositions[1] };

	const [start, end] = expandPositionsToDocDepth({
		doc: state.doc,
		startPos,
		endPos,
	});

	const decorations: Decoration[] = [];

	state.doc.nodesBetween(start, end, (node, pos) => {
		if (node.isBlock) {
			decorations.push(createHiddenNodeDecoration(pos, pos + node.nodeSize));
		}
		return false;
	});

	return decorations;
};

export const endInnerEditor = createCommand({
	type: 'END',
});
